/**
 * The terminal: an item grid, the open checks, and the moment one becomes
 * money.
 *
 * Laid out as grid-beside-check rather than as a wizard, because that is
 * what the job is. A server is standing up, has four things to ring and a
 * member waiting, and every step that makes them navigate is a step they
 * will do wrong at half past seven on a Saturday. Everything on this
 * screen is one tap from everything else.
 *
 * Three things this screen deliberately does NOT do:
 *
 *   * It never sends a price. The price is resolved on the server from the
 *     check's price book and outlet and frozen onto the line. A terminal
 *     that could name a price is a terminal where a price can be typed
 *     wrong and the check still adds up.
 *   * It never decides whether a member may be charged. The limit, the
 *     collections flag and the charging permission are the ledger's, and
 *     the refusal is shown as written — "charge of 4,300 would exceed the
 *     credit limit on account 1044" is the whole answer, and a paraphrase
 *     throws away the part the person at the desk has to say out loud.
 *   * It does not work offline. The beverage cart will need a real queue
 *     and a replay, and pretending otherwise with a cache would lose an
 *     order rather than refuse one. Until that is built, no signal means
 *     no ring, visibly.
 *
 * The open checks are faces, not rows. Nobody at a club remembers that
 * the Whitfields are on 14; they remember the four people at the window,
 * and the POS the club already uses shows them a photograph. The number
 * stays on the tile because the kitchen still calls it 14, but the thing
 * the eye lands on is the face.
 *
 * Every mutation returns the whole check, so the screen never has to
 * refetch to find out what it just did — and a second server's line that
 * landed in between arrives with it rather than going missing from the
 * screen of somebody about to close.
 */

import { useMemo, useState } from 'react';
import { api } from '../lib/api.ts';
import { useApi, useMutation } from '../lib/useApi.ts';
import { useSession } from '../lib/session.tsx';
import { money, type CurrencyInfo } from '../lib/format.ts';
import { Card, Empty, ErrorBanner, Loading, Money, Status } from '../components/ui.tsx';
import { Face, FaceRow } from '../components/Face.tsx';

/* ------------------------------------------------------------------ */
/* Shapes                                                              */
/* ------------------------------------------------------------------ */

interface Outlet {
  id: string;
  code: string;
  name: string;
  default_menu_id: string | null;
  default_menu_name: string | null;
  open_tabs: number;
}

interface PriceBook {
  id: string;
  code: string;
  name: string;
  applies_to: string;
  is_default: boolean;
}

interface MenuItem {
  menu_id: string;
  menu_name: string;
  section_id: string | null;
  section_name: string | null;
  section_sort: number | null;
  item_id: string;
  item_code: string;
  item_name: string;
  revenue_category: string;
  prep_station: string | null;
  is_alcohol: boolean;
  needs_authorization: boolean;
  is_available: boolean;
  price_minor: number | null;
}

interface ModifierOption {
  id: string;
  code: string;
  name: string;
  price_delta_minor: number;
  is_default: boolean;
}

interface ModifierGroup {
  item_id: string;
  group_id: string;
  code: string;
  name: string;
  min_choices: number;
  max_choices: number;
  options: ModifierOption[];
}

interface Tab {
  tab_id: string;
  outlet_id: string;
  outlet_name: string;
  business_date: string;
  tab_number: number;
  label: string | null;
  status: string;
  price_book_code: string;
  member_account_id: string | null;
  account_name: string | null;
  account_number: string | null;
  account_photo_uri: string | null;
  faces?: { person_id: string; name: string; photo_uri: string | null }[];
  service_charge_bp: number;
  covers: number | null;
  line_count: number;
  unposted_minor: number;
  posted_minor: number;
  comped_minor: number;
  voided_minor: number;
}

interface Line {
  line_id: string;
  seat_id: string | null;
  seat_number: number | null;
  item_name: string;
  description: string;
  quantity: string;
  unit_amount_minor: number;
  modifier_delta_minor: number;
  extended_minor: number;
  status: string;
  void_reason: string | null;
  comp_reason: string | null;
  sent_at: string | null;
  posting_id: string | null;
  note: string | null;
  modifiers: string[];
}

interface Seat {
  id: string;
  seat_number: number;
  label: string | null;
  member_account_id: string | null;
  person_id: string | null;
  account_name: string | null;
  account_number: string | null;
  person_name: string | null;
  photo_uri: string | null;
  has_posted: boolean;
}

interface Posting {
  id: string;
  seat_id: string | null;
  member_account_id: string;
  ar_transaction_id: string;
  amount_minor: number;
  account_name: string | null;
  account_photo_uri: string | null;
}

interface TabPayload {
  tab: Tab;
  lines: Line[];
  seats: Seat[];
  postings: Posting[];
}

interface AccountOption {
  member_account_id: string;
  account_number: string;
  display_name: string;
  status: string;
}

/** What GET /v1/members/:id already returns about a household. */
interface Household {
  account: { id: string; display_name: string; photo_uri: string | null };
  people: {
    id: string;
    first_name: string;
    last_name: string;
    preferred_name: string | null;
    photo_uri: string | null;
    may_charge: boolean;
    is_minor: boolean;
    is_active: boolean;
  }[];
}

function personName(p: Household['people'][number]): string {
  return `${p.preferred_name || p.first_name} ${p.last_name}`;
}

/* ------------------------------------------------------------------ */
/* The page                                                            */
/* ------------------------------------------------------------------ */

export function FnbTerminalPage() {
  const { currency } = useSession();

  const [outletId, setOutletId] = useState('');
  const [tab, setTab] = useState<TabPayload | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [pending, setPending] = useState<MenuItem | null>(null);

  const outlets = useApi<{ outlets: Outlet[] }>('/v1/fnb/outlets');
  const books = useApi<{ price_books: PriceBook[] }>('/v1/fnb/price-books');

  // Opening on the first counter alphabetically put a server in front of
  // the banquet department, which serves no menu, so the terminal's first
  // impression was an empty grid and a sentence about administration. The
  // default is the first counter that has something to sell — an open
  // check there first, then a menu — and only then whatever is first.
  const outlet =
    outlets.data?.outlets.find((o) => o.id === outletId) ??
    outlets.data?.outlets.find((o) => o.default_menu_id && o.open_tabs > 0) ??
    outlets.data?.outlets.find((o) => o.default_menu_id) ??
    outlets.data?.outlets[0];

  const openTabs = useApi<{ tabs: Tab[] }>(
    outlet ? `/v1/fnb/tabs?status=open&outlet_id=${outlet.id}` : null,
    [outlet?.id, tab?.tab.status, tab?.tab.tab_id],
  );

  const menu = useApi<{
    price_book_id: string;
    items: MenuItem[];
    modifier_groups: ModifierGroup[];
  }>(outlet ? `/v1/fnb/menus?outlet_id=${outlet.id}` : null, [outlet?.id]);

  const groupsByItem = useMemo(() => {
    const m = new Map<string, ModifierGroup[]>();
    for (const g of menu.data?.modifier_groups ?? []) {
      const list = m.get(g.item_id) ?? [];
      list.push(g);
      m.set(g.item_id, list);
    }
    return m;
  }, [menu.data]);

  const sections = useMemo(() => {
    const order: { key: string; name: string; items: MenuItem[] }[] = [];
    for (const it of menu.data?.items ?? []) {
      const key = it.section_id ?? 'none';
      let s = order.find((x) => x.key === key);
      if (!s) {
        s = { key, name: it.section_name ?? 'Everything else', items: [] };
        order.push(s);
      }
      s.items.push(it);
    }
    return order;
  }, [menu.data]);

  /* ---------------- mutations ---------------- */

  const open = useMutation(async (label: string) => {
    const result = await api<TabPayload>('/v1/fnb/tabs', {
      method: 'POST',
      body: { outlet_id: outlet!.id, label: label.trim() || null },
    });
    setTab(result);
    setNotice(null);
    openTabs.reload();
    return result;
  });

  const ring = useMutation(async (item: MenuItem, optionIds: string[], quantity: number) => {
    const result = await api<TabPayload>(`/v1/fnb/tabs/${tab!.tab.tab_id}/lines`, {
      method: 'POST',
      body: {
        item_id: item.item_id,
        quantity,
        option_ids: optionIds.length ? optionIds : undefined,
      },
    });
    setTab(result);
    setPending(null);
    return result;
  });

  const send = useMutation(async () => {
    const result = await api<TabPayload & { sent: number }>(
      `/v1/fnb/tabs/${tab!.tab.tab_id}/send`,
      { method: 'POST' },
    );
    setTab(result);
    setNotice(
      result.sent === 0
        ? 'Everything on this check has already gone to the kitchen.'
        : `${result.sent} line${result.sent === 1 ? '' : 's'} sent to the kitchen.`,
    );
    return result;
  });

  function load(tabId: string) {
    api<TabPayload>(`/v1/fnb/tabs/${tabId}`).then((t) => {
      setTab(t);
      setNotice(null);
    });
  }

  /* ---------------- render ---------------- */

  if (outlets.loading) return <Loading />;

  return (
    <>
      <h1 style={{ marginBottom: 16 }}>Terminal</h1>

      <div className="row" style={{ marginBottom: 16, alignItems: 'flex-end', gap: 12 }}>
        <label className="field" style={{ maxWidth: 260 }}>
          <span>Counter</span>
          <select
            value={outlet?.id ?? ''}
            onChange={(e) => {
              setOutletId(e.target.value);
              setTab(null);
            }}
          >
            {(outlets.data?.outlets ?? []).map((o) => (
              <option key={o.id} value={o.id}>
                {o.name}
                {o.open_tabs > 0 ? ` · ${o.open_tabs} open` : ''}
              </option>
            ))}
          </select>
        </label>

        <NewCheck
          busy={open.busy}
          disabled={!outlet}
          onOpen={(label) => open.run(label)}
        />

        <div className="spacer" />

        {books.data && books.data.price_books.length > 1 && tab && (
          <div className="muted small">
            Priced from the <strong>{tab.tab.price_book_code}</strong> book
          </div>
        )}
      </div>

      <ErrorBanner error={open.error} />
      <ErrorBanner error={ring.error} />
      <ErrorBanner error={send.error} />
      {notice && <div className="ok-banner">{notice}</div>}

      <div className="grid-2">
        {/* ---------------- the grid ---------------- */}
        <div>
          {!tab || tab.tab.status !== 'open' ? (
            <Card title="Open Checks" flush>
              {openTabs.loading ? (
                <div style={{ padding: 16 }}>
                  <Loading rows={4} />
                </div>
              ) : (openTabs.data?.tabs.length ?? 0) === 0 ? (
                <div style={{ padding: 16 }}>
                  <Empty>Nothing open at this counter. Open a check to start ringing.</Empty>
                </div>
              ) : (
                /* Tiles, not rows. The whole tile is the target — a
                   server reaching across a station hits a face, not a
                   4mm line of text — and the face is what they were
                   looking for in the first place. */
                <div className="checks" style={{ padding: 12 }}>
                  {(openTabs.data?.tabs ?? []).map((t) => (
                    <button
                      key={t.tab_id}
                      className="check-tile"
                      onClick={() => load(t.tab_id)}
                      title={`${t.label ?? 'Check'} · ${t.account_name ?? 'nobody named'}`}
                    >
                      <FaceRow
                        faces={t.faces ?? []}
                        fallbackName={t.account_name}
                        fallbackPhoto={t.account_photo_uri}
                        size={46}
                      />
                      <span className="check-tile__body">
                        <span className="check-tile__who">
                          {t.account_name ?? (
                            <span className="muted">Nobody named yet</span>
                          )}
                        </span>
                        <span className="muted small">
                          {t.label ?? 'No table'} · check {t.tab_number}
                        </span>
                      </span>
                      <span className="check-tile__total num">
                        <Money minor={t.unposted_minor} currency={currency} />
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </Card>
          ) : menu.loading ? (
            <Loading rows={6} />
          ) : (
            // Titled by the counter rather than by a menu: the bar serves
            // the bar list AND lunch, and naming the card after whichever
            // menu happened to sort first said "Bar" over a grid half
            // full of sandwiches.
            <Card title={outlet?.name ?? 'Menu'} flush>
              {sections.length === 0 ? (
                <div style={{ padding: 16 }}>
                  <Empty>
                    No menu is served at this counter yet. A menu is set up under Food &amp;
                    Beverage administration and pointed at a counter there.
                  </Empty>
                </div>
              ) : (
                <div style={{ padding: 12 }}>
                  {sections.map((s) => (
                    <div key={s.key} style={{ marginBottom: 16 }}>
                      <div className="grid-section">{s.name}</div>
                      <div className="grid-items">
                        {s.items.map((it) => (
                          <ItemButton
                            key={it.item_id}
                            item={it}
                            currency={currency}
                            busy={ring.busy}
                            onPick={() => {
                              const groups = groupsByItem.get(it.item_id) ?? [];
                              if (groups.length > 0) setPending(it);
                              else ring.run(it, [], 1);
                            }}
                          />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          )}
        </div>

        {/* ---------------- the check ---------------- */}
        <div>
          {tab ? (
            <CheckPanel
              payload={tab}
              currency={currency}
              onChanged={(next, message) => {
                setTab(next);
                if (message !== undefined) setNotice(message);
                openTabs.reload();
              }}
              onSend={() => send.run()}
              sending={send.busy}
              onClose={() => {
                setTab(null);
                openTabs.reload();
              }}
            />
          ) : (
            <Card title="No Check Open">
              <Empty>
                Pick a check on the left, or open a new one. Nothing rung onto a check reaches
                a member&rsquo;s account until it is closed.
              </Empty>
            </Card>
          )}
        </div>
      </div>

      {pending && (
        <ModifierPrompt
          item={pending}
          groups={groupsByItem.get(pending.item_id) ?? []}
          currency={currency}
          busy={ring.busy}
          onCancel={() => setPending(null)}
          onConfirm={(optionIds, quantity) => ring.run(pending, optionIds, quantity)}
        />
      )}
    </>
  );
}

/* ------------------------------------------------------------------ */
/* One item on the grid                                                */
/* ------------------------------------------------------------------ */

function ItemButton({
  item,
  currency,
  busy,
  onPick,
}: {
  item: MenuItem;
  currency: CurrencyInfo;
  busy: boolean;
  onPick: () => void;
}) {
  // Unpriced and eighty-sixed both mean "cannot be rung", and both are
  // shown rather than hidden: an item that vanishes reads as a bug, and
  // the server would refuse it anyway (POS-3). Finding that out by
  // tapping is worse than seeing it.
  const unsellable = !item.is_available || item.price_minor === null;

  return (
    <button
      className="secondary"
      disabled={unsellable || busy}
      onClick={onPick}
      title={
        item.price_minor === null
          ? 'Nobody has priced this yet, so it cannot be rung.'
          : !item.is_available
            ? 'The kitchen is out of this today.'
            : undefined
      }
    >
      <div style={{ fontWeight: 600 }}>{item.item_name}</div>
      <div className="muted small">
        {item.price_minor === null ? 'no price' : money(item.price_minor, currency)}
        {item.is_alcohol ? ' · alcohol' : ''}
        {!item.is_available ? ' · 86ed' : ''}
      </div>
    </button>
  );
}

/* ------------------------------------------------------------------ */
/* Opening a check                                                     */
/* ------------------------------------------------------------------ */

function NewCheck({
  busy,
  disabled,
  onOpen,
}: {
  busy: boolean;
  disabled: boolean;
  onOpen: (label: string) => void;
}) {
  const [label, setLabel] = useState('');
  return (
    <div className="row" style={{ gap: 8, alignItems: 'flex-end' }}>
      <label className="field" style={{ maxWidth: 200 }}>
        <span>New check</span>
        <input
          placeholder="Table 4, Chair 12, 7th fairway"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
        />
      </label>
      <button
        disabled={disabled || busy}
        onClick={() => {
          onOpen(label);
          setLabel('');
        }}
      >
        Open a check
      </button>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Answering the kitchen's questions                                   */
/* ------------------------------------------------------------------ */

function ModifierPrompt({
  item,
  groups,
  currency,
  busy,
  onCancel,
  onConfirm,
}: {
  item: MenuItem;
  groups: ModifierGroup[];
  currency: CurrencyInfo;
  busy: boolean;
  onCancel: () => void;
  onConfirm: (optionIds: string[], quantity: number) => void;
}) {
  const [chosen, setChosen] = useState<Record<string, string[]>>(() => {
    const initial: Record<string, string[]> = {};
    for (const g of groups) {
      const d = g.options.filter((o) => o.is_default).map((o) => o.id);
      if (d.length) initial[g.group_id] = d.slice(0, g.max_choices);
    }
    return initial;
  });
  const [quantity, setQuantity] = useState(1);

  const ids = Object.values(chosen).flat();
  const delta = groups
    .flatMap((g) => g.options)
    .filter((o) => ids.includes(o.id))
    .reduce((sum, o) => sum + o.price_delta_minor, 0);

  // Only the minimum is checked here, because only the minimum can be
  // shown as "not yet". Over the maximum is impossible to reach: a
  // one-choice group replaces rather than adds.
  const unanswered = groups.filter(
    (g) => (chosen[g.group_id]?.length ?? 0) < g.min_choices,
  );

  function toggle(g: ModifierGroup, optionId: string) {
    setChosen((c) => {
      const current = c[g.group_id] ?? [];
      if (current.includes(optionId)) {
        return { ...c, [g.group_id]: current.filter((x) => x !== optionId) };
      }
      if (g.max_choices === 1) return { ...c, [g.group_id]: [optionId] };
      if (current.length >= g.max_choices) return c;
      return { ...c, [g.group_id]: [...current, optionId] };
    });
  }

  return (
    <div className="overlay" onClick={onCancel}>
      <div className="dialog" onClick={(e) => e.stopPropagation()}>
        <div className="dialog__body">
          <h2 style={{ marginTop: 0 }}>{item.item_name}</h2>

          {groups.map((g) => (
            <div key={g.group_id} style={{ marginBottom: 14 }}>
              <div className="field-label">
                {g.name}
                <span className="muted small">
                  {g.min_choices > 0
                    ? ` · pick ${g.min_choices === g.max_choices ? g.min_choices : `${g.min_choices}–${g.max_choices}`}`
                    : ` · up to ${g.max_choices}, optional`}
                </span>
              </div>
              <div className="chips">
                {g.options.map((o) => {
                  const on = (chosen[g.group_id] ?? []).includes(o.id);
                  return (
                    <button
                      key={o.id}
                      className={on ? '' : 'secondary'}
                      onClick={() => toggle(g, o.id)}
                    >
                      {o.name}
                      {o.price_delta_minor !== 0
                        ? ` ${o.price_delta_minor > 0 ? '+' : '−'}${money(Math.abs(o.price_delta_minor), currency)}`
                        : ''}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}

          <div className="row" style={{ gap: 12, alignItems: 'flex-end' }}>
            <label className="field" style={{ maxWidth: 90 }}>
              <span>How many</span>
              <input
                type="number"
                min={1}
                max={99}
                value={quantity}
                onChange={(e) => setQuantity(Math.max(1, Number(e.target.value) || 1))}
              />
            </label>
            <div className="muted">
              {money((item.price_minor ?? 0) + delta, currency)} each
            </div>
          </div>

          {unanswered.length > 0 && (
            <div className="notice" style={{ marginTop: 12 }}>
              The kitchen needs {unanswered.map((g) => g.name.toLowerCase()).join(' and ')}{' '}
              before this ticket can go.
            </div>
          )}
        </div>

        <div className="dialog__actions">
          <button
            disabled={busy || unanswered.length > 0}
            onClick={() => onConfirm(ids, quantity)}
          >
            Ring it
          </button>
          <button className="secondary" onClick={onCancel}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* The check itself                                                    */
/* ------------------------------------------------------------------ */

function CheckPanel({
  payload,
  currency,
  onChanged,
  onSend,
  sending,
  onClose,
}: {
  payload: TabPayload;
  currency: CurrencyInfo;
  onChanged: (next: TabPayload, message?: string) => void;
  onSend: () => void;
  sending: boolean;
  onClose: () => void;
}) {
  const { tab, lines, seats, postings } = payload;
  const [closing, setClosing] = useState(false);
  const [seatFor, setSeatFor] = useState<string | null>(null);
  const [voiding, setVoiding] = useState<Line | null>(null);

  const unsent = lines.filter((l) => l.status === 'active' && !l.sent_at).length;
  const serviceCharge =
    tab.service_charge_bp > 0
      ? Math.round((tab.unposted_minor * tab.service_charge_bp) / 10000)
      : 0;

  return (
    <>
      <Card
        title={`Check ${tab.tab_number}${tab.label ? ` · ${tab.label}` : ''}`}
        actions={
          <div className="actions">
            {unsent > 0 && (
              <button className="small" disabled={sending} onClick={onSend}>
                Send {unsent} to the kitchen
              </button>
            )}
            <button className="secondary small" onClick={onClose}>
              Close panel
            </button>
          </div>
        }
        flush
      >
        <div style={{ padding: '10px 16px', gap: 12 }} className="row">
          <Face
            name={tab.account_name}
            photoUri={tab.account_photo_uri}
            size={40}
            dim={tab.status !== 'open'}
          />
          <div>
            <div style={{ fontWeight: 600 }}>
              {tab.account_name ?? <span className="muted">Nobody named yet</span>}
            </div>
            <div className="muted small">
              {tab.outlet_name} · {tab.business_date}
              {tab.covers ? ` · ${tab.covers} covers` : ''}
            </div>
          </div>
          <div className="spacer" />
          <Status tone={tab.status === 'open' ? 'good' : 'neutral'}>
            {tab.status === 'open' ? 'Open' : tab.status === 'closed' ? 'Closed' : 'Voided'}
          </Status>
        </div>

        {lines.length === 0 ? (
          <div style={{ padding: 16 }}>
            <Empty>Nothing rung yet.</Empty>
          </div>
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th style={{ width: 44 }}>Qty</th>
                <th>Item</th>
                <th style={{ width: 56 }}>Seat</th>
                <th className="num" style={{ width: 90 }}>
                  Amount
                </th>
                <th style={{ width: 120 }} />
              </tr>
            </thead>
            <tbody>
              {lines.map((l) => (
                <tr
                  key={l.line_id}
                  style={{ opacity: l.status === 'active' ? 1 : 0.55 }}
                >
                  <td className="num">{Number(l.quantity)}</td>
                  <td>
                    <div
                      style={{
                        textDecoration: l.status === 'voided' ? 'line-through' : undefined,
                      }}
                    >
                      {l.description}
                    </div>
                    {l.modifiers.length > 0 && (
                      <div className="muted small">{l.modifiers.join(', ')}</div>
                    )}
                    {l.note && <div className="muted small">{l.note}</div>}
                    {l.status === 'voided' && (
                      <div className="muted small">Voided — {l.void_reason}</div>
                    )}
                    {l.status === 'comped' && (
                      <div className="muted small">Comped — {l.comp_reason}</div>
                    )}
                    {l.posting_id && <div className="muted small">Charged</div>}
                  </td>
                  <td className="num">{l.seat_number ?? '—'}</td>
                  <td className="num">
                    <Money minor={l.extended_minor} currency={currency} />
                  </td>
                  <td>
                    {l.status === 'active' && !l.posting_id && (
                      <button className="secondary small" onClick={() => setVoiding(l)}>
                        Void / comp
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        <div style={{ padding: '12px 16px' }}>
          <div className="pairs">
            <div>Still to charge</div>
            <div className="num">
              <Money minor={tab.unposted_minor} currency={currency} />
            </div>
            {tab.service_charge_bp > 0 && (
              <>
                <div>Service charge ({tab.service_charge_bp / 100}%)</div>
                <div className="num">
                  <Money minor={serviceCharge} currency={currency} />
                </div>
              </>
            )}
            {tab.posted_minor > 0 && (
              <>
                <div>Already charged</div>
                <div className="num">
                  <Money minor={tab.posted_minor} currency={currency} />
                </div>
              </>
            )}
            {tab.comped_minor > 0 && (
              <>
                <div>Comped</div>
                <div className="num">
                  <Money minor={tab.comped_minor} currency={currency} />
                </div>
              </>
            )}
          </div>

          {/* Tax is not previewed. The engine resolves the rate in force on
              the capture date and that is the number that binds; a second
              implementation here would eventually disagree with it, and the
              member would be told the wrong total by the more confident of
              the two. */}
          <div className="hint" style={{ marginTop: 8 }}>
            Any sales tax is added when the check is charged.
          </div>

          {tab.status === 'open' && tab.unposted_minor > 0 && (
            <div className="actions" style={{ marginTop: 12 }}>
              <button onClick={() => setClosing(true)}>Charge to an account</button>
            </div>
          )}
        </div>
      </Card>

      {postings.length > 0 && (
        <Card title="Charged">
          <table className="data">
            <tbody>
              {postings.map((p) => (
                <tr key={p.id}>
                  <td style={{ width: 38 }}>
                    <Face name={p.account_name} photoUri={p.account_photo_uri} size={28} />
                  </td>
                  <td>{p.account_name}</td>
                  <td>{p.seat_id ? `seat ${seats.find((s) => s.id === p.seat_id)?.seat_number ?? ''}` : 'whole check'}</td>
                  <td className="num">
                    <Money minor={p.amount_minor} currency={currency} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {tab.status === 'open' && (
        <Seats
          payload={payload}
          currency={currency}
          onChanged={onChanged}
          onCloseSeat={(seatId) => setSeatFor(seatId)}
        />
      )}

      {(closing || seatFor) && (
        <CloseCheck
          payload={payload}
          seatId={seatFor}
          currency={currency}
          onCancel={() => {
            setClosing(false);
            setSeatFor(null);
          }}
          onDone={(next, message) => {
            setClosing(false);
            setSeatFor(null);
            onChanged(next, message);
          }}
        />
      )}

      {voiding && (
        <VoidOrComp
          line={voiding}
          onCancel={() => setVoiding(null)}
          onDone={(next, message) => {
            setVoiding(null);
            onChanged(next, message);
          }}
        />
      )}
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Seats — which is to say, who signs                                  */
/* ------------------------------------------------------------------ */

function Seats({
  payload,
  currency,
  onChanged,
  onCloseSeat,
}: {
  payload: TabPayload;
  currency: CurrencyInfo;
  onChanged: (next: TabPayload, message?: string) => void;
  onCloseSeat: (seatId: string) => void;
}) {
  const { tab, lines, seats } = payload;
  const [search, setSearch] = useState('');
  const [accountId, setAccountId] = useState('');
  const [personId, setPersonId] = useState('');

  const accounts = useApi<{ accounts: AccountOption[] }>(
    `/v1/member-accounts?limit=25${search ? `&search=${encodeURIComponent(search)}` : ''}`,
    [search],
  );

  // The household behind the chosen account, so the desk can say WHICH
  // of them is in the chair. /v1/members/:id already returns the people
  // with their photographs and whether each may charge — this screen
  // does not need its own idea of a family.
  const household = useApi<Household>(accountId ? `/v1/members/${accountId}` : null, [accountId]);

  const chargers = (household.data?.people ?? []).filter((p) => p.may_charge && p.is_active);

  const add = useMutation(async () => {
    const next = await api<TabPayload>(`/v1/fnb/tabs/${tab.tab_id}/seats`, {
      method: 'POST',
      body: { member_account_id: accountId || null, person_id: personId || null },
    });
    setAccountId('');
    setPersonId('');
    setSearch('');
    onChanged(next);
    return next;
  });

  function seatTotal(seatId: string) {
    return lines
      .filter((l) => l.seat_id === seatId && l.status === 'active' && !l.posting_id)
      .reduce((sum, l) => sum + l.extended_minor, 0);
  }

  return (
    <Card title="Seats">
      <ErrorBanner error={add.error} />

      {/* A seat is worth a row only because at a club the seat is who
          pays. Four members at lunch are four statements, and the
          alternative is the desk opening four checks and copying the
          order onto each. */}
      {seats.length === 0 ? (
        <Empty>
          No seats. The whole check charges to one account — add seats when the table is
          splitting.
        </Empty>
      ) : (
        <table className="data">
          <tbody>
            {seats.map((s) => (
              <tr key={s.id}>
                <td className="num mono">{s.seat_number}</td>
                <td style={{ width: 42 }}>
                  <Face
                    name={s.person_name ?? s.account_name}
                    photoUri={s.photo_uri}
                    size={32}
                    dim={s.has_posted}
                  />
                </td>
                <td>
                  {s.person_name ?? s.account_name ?? (
                    <span className="muted">nobody named</span>
                  )}
                  {s.person_name && s.account_name && s.person_name !== s.account_name && (
                    <div className="muted small">signs to {s.account_name}</div>
                  )}
                </td>
                <td className="num">
                  <Money minor={seatTotal(s.id)} currency={currency} />
                </td>
                <td>
                  {s.has_posted ? (
                    <span className="muted small">signed</span>
                  ) : (
                    seatTotal(s.id) > 0 && (
                      <button className="secondary small" onClick={() => onCloseSeat(s.id)}>
                        Charge this seat
                      </button>
                    )
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <div className="row" style={{ gap: 8, alignItems: 'flex-end', marginTop: 12 }}>
        <label className="field" style={{ maxWidth: 170 }}>
          <span>Find a member</span>
          <input
            placeholder="Name or number"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </label>
        <label className="field" style={{ maxWidth: 240 }}>
          <span>Signs for this seat</span>
          <select
            value={accountId}
            onChange={(e) => {
              setAccountId(e.target.value);
              setPersonId('');
            }}
          >
            <option value="">Nobody yet</option>
            {(accounts.data?.accounts ?? []).map((a) => (
              <option key={a.member_account_id} value={a.member_account_id}>
                {a.display_name} · {a.account_number}
              </option>
            ))}
          </select>
        </label>
        <button className="secondary" disabled={add.busy} onClick={() => add.run()}>
          Add a seat
        </button>
      </div>

      {/* Which of them is in the chair. The account is a family; the
          seat is a person, and the person is whose face the next server
          to walk past will recognize. Optional — a seat charged to the
          household with nobody named is still a valid seat. */}
      {accountId !== '' && chargers.length > 0 && (
        <div style={{ marginTop: 10 }}>
          <div className="field-label">Who is sitting there</div>
          <div className="chips">
            <button
              className={personId === '' ? '' : 'secondary'}
              onClick={() => setPersonId('')}
            >
              Not saying
            </button>
            {chargers.map((p) => (
              <button
                key={p.id}
                className={personId === p.id ? '' : 'secondary'}
                onClick={() => setPersonId(p.id)}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}
              >
                <Face name={personName(p)} photoUri={p.photo_uri} size={22} />
                {personName(p)}
              </button>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
}

/* ------------------------------------------------------------------ */
/* Charging it                                                         */
/* ------------------------------------------------------------------ */

function CloseCheck({
  payload,
  seatId,
  currency,
  onCancel,
  onDone,
}: {
  payload: TabPayload;
  seatId: string | null;
  currency: CurrencyInfo;
  onCancel: () => void;
  onDone: (next: TabPayload, message: string) => void;
}) {
  const { tab, seats, lines } = payload;
  const seat = seats.find((s) => s.id === seatId) ?? null;

  const [search, setSearch] = useState('');
  const [accountId, setAccountId] = useState(
    seat?.member_account_id ?? tab.member_account_id ?? '',
  );
  // One key per attempt at this close. A double-tapped button, a browser
  // retry and a dropped response all resolve to the same charge.
  const [key] = useState(() => `web-close-${crypto.randomUUID()}`);

  const accounts = useApi<{ accounts: AccountOption[] }>(
    `/v1/member-accounts?limit=25${search ? `&search=${encodeURIComponent(search)}` : ''}`,
    [search],
  );

  // The face of whoever is about to be charged. This is the one moment
  // in the module where getting the wrong member costs somebody an
  // argument about their statement, and a name in a dropdown is two
  // Whitfields away from a mistake nobody notices for a month.
  const household = useApi<Household>(accountId ? `/v1/members/${accountId}` : null, [accountId]);

  const amount = lines
    .filter(
      (l) =>
        l.status === 'active' &&
        !l.posting_id &&
        (seatId === null || l.seat_id === seatId),
    )
    .reduce((sum, l) => sum + l.extended_minor, 0);

  const close = useMutation(async () => {
    const next = await api<TabPayload & { charge: { amount_minor: number } | null }>(
      `/v1/fnb/tabs/${tab.tab_id}/close`,
      {
        method: 'POST',
        body: {
          member_account_id: accountId || null,
          seat_id: seatId,
          idempotency_key: key,
        },
      },
    );
    onDone(
      next,
      `Charged ${money(next.charge?.amount_minor ?? amount, currency)} to the account.`,
    );
    return next;
  });

  const chosen = accounts.data?.accounts.find((a) => a.member_account_id === accountId);

  return (
    <div className="overlay" onClick={onCancel}>
      <div className="dialog" onClick={(e) => e.stopPropagation()}>
        <div className="dialog__body">
          <h2 style={{ marginTop: 0 }}>
            {seat ? `Charge seat ${seat.seat_number}` : 'Charge the check'}
          </h2>

          <ErrorBanner error={close.error} />

          <div className="pairs" style={{ marginBottom: 12 }}>
            <div>Before tax{tab.service_charge_bp > 0 ? ' and service' : ''}</div>
            <div className="num">
              <Money minor={amount} currency={currency} />
            </div>
          </div>

          <label className="field">
            <span>Find the member</span>
            <input
              placeholder="Name or account number"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </label>

          <label className="field">
            <span>Charge to</span>
            <select value={accountId} onChange={(e) => setAccountId(e.target.value)}>
              <option value="">Choose an account…</option>
              {(accounts.data?.accounts ?? []).map((a) => (
                <option key={a.member_account_id} value={a.member_account_id}>
                  {a.display_name} · {a.account_number}
                  {a.status !== 'current' ? ` (${a.status.replace('_', ' ')})` : ''}
                </option>
              ))}
            </select>
          </label>

          {accountId !== '' && (
            <div
              className="row"
              style={{ gap: 12, alignItems: 'center', margin: '10px 0 4px' }}
            >
              <Face
                name={household.data?.account.display_name ?? chosen?.display_name}
                photoUri={household.data?.account.photo_uri}
                size={52}
              />
              <div>
                <div style={{ fontWeight: 600 }}>
                  {household.data?.account.display_name ?? chosen?.display_name ?? '…'}
                </div>
                {(household.data?.people ?? []).filter((p) => p.may_charge && p.is_active)
                  .length > 0 && (
                  <div className="muted small">
                    {(household.data?.people ?? [])
                      .filter((p) => p.may_charge && p.is_active)
                      .map(personName)
                      .join(', ')}
                  </div>
                )}
              </div>
            </div>
          )}

          {chosen && chosen.status !== 'current' && (
            <div className="notice">
              This account is <strong>{chosen.status.replace('_', ' ')}</strong>. Whether it
              may be charged is the ledger&rsquo;s decision, not this screen&rsquo;s — it will
              say so if it refuses.
            </div>
          )}
        </div>

        <div className="dialog__actions">
          <button disabled={close.busy || !accountId} onClick={() => close.run()}>
            {close.busy ? 'Charging…' : 'Charge it'}
          </button>
          <button className="secondary" onClick={onCancel}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Taking something off                                                */
/* ------------------------------------------------------------------ */

function VoidOrComp({
  line,
  onCancel,
  onDone,
}: {
  line: Line;
  onCancel: () => void;
  onDone: (next: TabPayload, message: string) => void;
}) {
  const [reason, setReason] = useState('');

  const act = useMutation(async (what: 'void' | 'comp') => {
    const next = await api<TabPayload>(`/v1/fnb/lines/${line.line_id}/${what}`, {
      method: 'POST',
      body: { reason: reason.trim() },
    });
    onDone(
      next,
      what === 'void'
        ? 'Struck off the check. It stays on the void report.'
        : 'Given away. It posts nothing, and the comp report has it.',
    );
    return next;
  });

  return (
    <div className="overlay" onClick={onCancel}>
      <div className="dialog" onClick={(e) => e.stopPropagation()}>
        <div className="dialog__body">
          <h2 style={{ marginTop: 0 }}>{line.description}</h2>
          <ErrorBanner error={act.error} />

          {/* Both need a reason, and the server refuses without one. A
              shift with forty voids is a conversation somebody should be
              having, and a blank reason is how it never happens. */}
          <label className="field">
            <span>Why</span>
            <input
              placeholder="Sent back — overcooked"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </label>

          <div className="hint">
            A void takes it off the check. A comp gives it away — it still costs the club
            food, and it shows on the comp report rather than in revenue.
          </div>
        </div>

        <div className="dialog__actions">
          <button disabled={act.busy || reason.trim().length < 2} onClick={() => act.run('void')}>
            Void it
          </button>
          <button
            className="secondary"
            disabled={act.busy || reason.trim().length < 2}
            onClick={() => act.run('comp')}
          >
            Comp it
          </button>
          <button className="secondary" onClick={onCancel}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
